import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { X, ChevronLeft, ChevronRight, Download, Trash2, Pencil } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { supabase } from '../lib/supabase';
import ImageEditor from './ImageEditor';

// ─────────────────────────────────────────────────────────────────────────────
// Global image viewer. Mount <ImageViewerProvider> once at the app root, then
// call `useImageViewer().open({ images, index })` from anywhere. Handles plain
// public URLs, auth-proxied media (e.g. Twilio MMS), blobs, and PDFs — with
// pinch/zoom/pan, swipe, keyboard nav, a thumbnail strip, download, and an
// optional `actions` slot (delete, etc.). Built to host editing tools later.
// ─────────────────────────────────────────────────────────────────────────────

export interface ViewerImage {
  /** Direct/public URL (or blob: URL). */
  src?: string;
  /** Full URL that must be fetched with Supabase auth headers (e.g. Twilio proxy). */
  proxy?: string;
  /** Suggested download filename. */
  download?: string;
  /** Caption shown in the header (sender name, "Photos for X", etc.). */
  caption?: string;
  kind?: 'image' | 'pdf';
}

export interface ViewerAction {
  label: string;
  icon?: React.ReactNode;
  /** Receives the index of the currently-viewed image. */
  onClick: (index: number) => void;
  destructive?: boolean;
}

interface OpenOptions {
  images: ViewerImage[];
  index?: number;
  actions?: ViewerAction[];
  /** Enable the markup editor (defaults to true for raster images). */
  editable?: boolean;
  /**
   * Persist an edited image. Receives the image index and the flattened JPEG
   * blob. If omitted, the edit is only applied in-memory for the current session.
   */
  onSaveEdit?: (index: number, blob: Blob) => void | Promise<void>;
}

interface ImageViewerContextValue {
  open: (opts: OpenOptions) => void;
  close: () => void;
}

const ImageViewerContext = createContext<ImageViewerContextValue | null>(null);

export function useImageViewer(): ImageViewerContextValue {
  const ctx = useContext(ImageViewerContext);
  if (!ctx) {
    // Safe no-op fallback if a caller is rendered outside the provider.
    return { open: () => {}, close: () => {} };
  }
  return ctx;
}

const ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string | undefined;

/** Resolve a viewer image to a displayable URL, fetching proxied media to a blob. */
async function resolveImageUrl(img: ViewerImage): Promise<string> {
  if (!img.proxy) return img.src || '';
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const headers: Record<string, string> = {};
    if (ANON_KEY) headers['apikey'] = ANON_KEY;
    if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
    const res = await fetch(img.proxy, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch (e) {
    console.error('[ImageViewer] proxy fetch failed:', e);
    return img.src || '';
  }
}

/** Parse a Supabase Storage URL into { bucket, path }, or null if not one. */
function parseSupabaseStoragePath(url?: string): { bucket: string; path: string } | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    const marker = '/storage/v1/object/';
    const i = u.pathname.indexOf(marker);
    if (i < 0) return null;
    let rest = u.pathname.slice(i + marker.length);
    rest = rest.replace(/^public\//, '').replace(/^sign\//, '').replace(/^authenticated\//, '');
    const decoded = decodeURIComponent(rest);
    const slash = decoded.indexOf('/');
    if (slash < 0) return null;
    return { bucket: decoded.slice(0, slash), path: decoded.slice(slash + 1) };
  } catch {
    return null;
  }
}

/**
 * Default persistence: if the image lives in Supabase Storage, overwrite the
 * original file in place so the same URL now serves the edited image. Returns
 * true if it persisted. (Twilio/blob/foreign URLs can't be written back.)
 */
async function persistEditedToStorage(img: ViewerImage | undefined, blob: Blob): Promise<boolean> {
  const parsed = parseSupabaseStoragePath(img?.src) || parseSupabaseStoragePath(img?.proxy);
  if (!parsed) return false;
  const { error } = await supabase.storage
    .from(parsed.bucket)
    .upload(parsed.path, blob, { upsert: true, contentType: 'image/jpeg', cacheControl: '0' });
  if (error) {
    console.error('[ImageViewer] storage overwrite failed:', error);
    return false;
  }
  return true;
}

async function downloadImage(url: string, filename: string) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    if (Capacitor.isNativePlatform()) {
      // Best-effort native share of the file.
      try {
        const { Share } = await import('@capacitor/share');
        const objectUrl = URL.createObjectURL(blob);
        await Share.share({ url: objectUrl, title: filename });
        return;
      } catch {
        /* fall through to anchor */
      }
    }
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'image';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
  } catch (e) {
    console.error('[ImageViewer] download failed:', e);
    window.open(url, '_blank');
  }
}

interface ViewerState {
  images: ViewerImage[];
  index: number;
  actions: ViewerAction[];
  editable: boolean;
  onSaveEdit?: (index: number, blob: Blob) => void | Promise<void>;
}

export function ImageViewerProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ViewerState | null>(null);

  const open = useCallback((opts: OpenOptions) => {
    if (!opts.images?.length) return;
    setState({
      images: opts.images,
      index: Math.min(Math.max(opts.index ?? 0, 0), opts.images.length - 1),
      actions: opts.actions || [],
      editable: opts.editable !== false,
      onSaveEdit: opts.onSaveEdit,
    });
  }, []);

  const close = useCallback(() => setState(null), []);

  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <ImageViewerContext.Provider value={value}>
      {children}
      {state && (
        <ImageViewer
          images={state.images}
          index={state.index}
          actions={state.actions}
          editable={state.editable}
          onSaveEdit={state.onSaveEdit}
          onClose={close}
        />
      )}
    </ImageViewerContext.Provider>
  );
}

function ImageViewer({
  images,
  index: initialIndex,
  actions,
  editable,
  onSaveEdit,
  onClose,
}: {
  images: ViewerImage[];
  index: number;
  actions: ViewerAction[];
  editable: boolean;
  onSaveEdit?: (index: number, blob: Blob) => void | Promise<void>;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(initialIndex);
  const [resolved, setResolved] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const zoomedRef = useRef(false);

  const current = images[index];
  const count = images.length;
  const go = useCallback((delta: number) => {
    setIndex((i) => {
      const next = i + delta;
      if (next < 0) return 0;
      if (next > count - 1) return count - 1;
      return next;
    });
  }, [count]);

  // Resolve current (and neighbors) to displayable URLs.
  useEffect(() => {
    let cancelled = false;
    const ensure = async (i: number) => {
      if (i < 0 || i > count - 1 || resolved[i] !== undefined) return;
      const url = await resolveImageUrl(images[i]);
      if (!cancelled) setResolved((prev) => ({ ...prev, [i]: url }));
    };
    (async () => {
      if (resolved[index] === undefined) setLoading(true);
      await ensure(index);
      if (!cancelled) setLoading(false);
      ensure(index + 1);
      ensure(index - 1);
    })();
    return () => { cancelled = true; };
  }, [index, images, count]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard navigation.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [go, onClose]);

  const currentUrl = resolved[index];
  const isPdf = current?.kind === 'pdf';

  return createPortal(
    <div
      className="fixed inset-0 z-[100200] bg-black/95 flex flex-col"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 text-white/90 shrink-0">
        <button onClick={onClose} className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-white/10 active:bg-white/20" aria-label="Close">
          <X className="h-6 w-6" />
        </button>
        <div className="flex-1 min-w-0 text-center">
          {current?.caption && <div className="truncate text-sm font-medium">{current.caption}</div>}
          {count > 1 && <div className="text-xs text-white/60 tabular-nums">{index + 1} / {count}</div>}
        </div>
        <div className="flex items-center gap-1">
          {actions.map((a, i) => (
            <button key={i} onClick={() => a.onClick(index)} title={a.label}
              className={`h-10 w-10 flex items-center justify-center rounded-full hover:bg-white/10 active:bg-white/20 ${a.destructive ? 'text-red-400' : ''}`}>
              {a.icon || (a.destructive ? <Trash2 className="h-5 w-5" /> : <span className="text-xs">{a.label}</span>)}
            </button>
          ))}
          {currentUrl && !isPdf && editable && (
            <button onClick={() => setEditing(true)} title="Edit"
              className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-white/10 active:bg-white/20">
              <Pencil className="h-5 w-5" />
            </button>
          )}
          {currentUrl && !isPdf && (
            <button onClick={() => downloadImage(currentUrl, current?.download || 'image')} title="Download"
              className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-white/10 active:bg-white/20">
              <Download className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Stage */}
      <div
        className="relative flex-1 min-h-0 flex items-center justify-center overflow-hidden"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        onTouchStart={(e) => { touchStartX.current = e.touches[0]?.clientX ?? null; }}
        onTouchEnd={(e) => {
          if (touchStartX.current == null || zoomedRef.current) { touchStartX.current = null; return; }
          const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
          if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
          touchStartX.current = null;
        }}
      >
        {count > 1 && (
          <>
            <button onClick={() => go(-1)} disabled={index === 0}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-11 w-11 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button onClick={() => go(1)} disabled={index === count - 1}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-11 w-11 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}

        {loading && !currentUrl && (
          <div className="h-10 w-10 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        )}

        {currentUrl && isPdf && (
          <iframe src={currentUrl} title="PDF" className="w-full h-full bg-white" />
        )}

        {currentUrl && !isPdf && (
          <TransformWrapper
            key={index}
            doubleClick={{ mode: 'toggle', step: 2 }}
            minScale={1}
            maxScale={5}
            centerOnInit
            onZoom={(ref) => { zoomedRef.current = ref.state.scale > 1.01; }}
            onTransformed={(ref) => { zoomedRef.current = ref.state.scale > 1.01; }}
          >
            <TransformComponent
              wrapperStyle={{ width: '100%', height: '100%' }}
              contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <img
                src={currentUrl}
                alt={current?.caption || ''}
                className="max-w-full max-h-full object-contain select-none"
                draggable={false}
              />
            </TransformComponent>
          </TransformWrapper>
        )}
      </div>

      {/* Thumbnail strip */}
      {count > 1 && (
        <div className="shrink-0 flex items-center gap-2 px-3 py-2 overflow-x-auto">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`h-12 w-12 shrink-0 rounded-lg overflow-hidden border-2 transition-all ${i === index ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'}`}
            >
              {resolved[i] && img.kind !== 'pdf'
                ? <img src={resolved[i]} alt="" className="h-full w-full object-cover" />
                : <div className="h-full w-full bg-white/10 flex items-center justify-center text-[10px] text-white/60">{img.kind === 'pdf' ? 'PDF' : i + 1}</div>}
            </button>
          ))}
        </div>
      )}

      {editing && currentUrl && (
        <ImageEditor
          src={currentUrl}
          onCancel={() => setEditing(false)}
          onSave={async (blob) => {
            try {
              if (onSaveEdit) await onSaveEdit(index, blob);
              else await persistEditedToStorage(current, blob);
            } catch (e) {
              console.error('[ImageViewer] save failed:', e);
            } finally {
              const url = URL.createObjectURL(blob);
              setResolved((prev) => ({ ...prev, [index]: url }));
              setEditing(false);
            }
          }}
        />
      )}
    </div>,
    document.body,
  );
}
